import React, { useState, useEffect } from "react";
import Select from "react-select";
import Sidebar from "../../components/sidebar/Sidebar";
import MainSection from "../../components/mainLayout/Main";
import { addClient, getAllServices } from "../../redux/actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

function AddClient() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const services = useSelector((state) => state.auth.allServices);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clientType: "",
    phoneNumber: "",
    password: "",
    opted: [],
  });
  const [isCustomPassword, setIsCustomPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) dispatch(getAllServices(token));
  }, [dispatch]);

  const computeDefaultPassword = (nameVal, phoneVal) => {
    const cleanName = (nameVal || "").replace(/\s+/g, "");
    const lastDigits = phoneVal && phoneVal.length >= 5 ? phoneVal.slice(-5) : (phoneVal || "");
    return cleanName + lastDigits;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData((prev) => {
          const updated = { ...prev, [id]: numericValue };
          if (!isCustomPassword) {
            updated.password = computeDefaultPassword(prev.name, numericValue);
          }
          return updated;
        });
      }
    } else if (id === "name") {
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
      if (lettersOnly.length <= 20) {
        setFormData((prev) => {
          const updated = { ...prev, [id]: lettersOnly };
          if (!isCustomPassword) {
            updated.password = computeDefaultPassword(lettersOnly, prev.phoneNumber);
          }
          return updated;
        });
      }
    } else if (id === "password") {
      setIsCustomPassword(true);
      setFormData((prev) => ({ ...prev, [id]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleResetPassword = () => {
    setIsCustomPassword(false);
    setFormData((prev) => ({
      ...prev,
      password: computeDefaultPassword(prev.name, prev.phoneNumber),
    }));
  };

  const handleSelectChange = (selectedOptions) => {
    setFormData({
      ...formData,
      opted: selectedOptions ? selectedOptions.map((option) => option.value) : [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.clientType)
      return message.error("Please select client type");
    if (formData.phoneNumber.length < 10)
      return message.error("Invalid Phone Number");
    if (formData.opted.length === 0)
      return message.error("Please select at least one service");
    try {
      await dispatch(addClient(formData));
      navigate("/client-management");
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCancel = () => {
    navigate("/client-management");
  };

  const serviceOptions =
    services?.map((service) => ({ value: service._id, label: service.name }));

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#f9fafb",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      borderRadius: "8px",
      padding: "2px",
      fontSize: "14px",
      minHeight: "38px",
      "&:hover": { borderColor: "#3b82f6" },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#dbeafe",
      borderRadius: "12px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#1e40af",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#eff6ff" : "white",
      color: "#1f2937",
      fontSize: "14px",
      "&:active": { backgroundColor: "#dbeafe" },
    }),
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <MainSection>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Add Client</h1>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="businessName" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
                      placeholder="Enter Company name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Client Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
                      placeholder="Enter Client name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="clientType" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Client Type
                    </label>
                    <select
                      id="clientType"
                      value={formData.clientType}
                      onChange={handleChange}
                      required
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    >
                      <option disabled value="">
                        Select Client Type
                      </option>
                      <option value="Private Limited">Private Limited</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Proprietorship">Proprietorship</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      placeholder="Enter Phone number"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label htmlFor="password" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Password
                      </label>
                      <div className="flex items-center space-x-2">
                        {isCustomPassword && (
                          <button
                            type="button"
                            onClick={handleResetPassword}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                          >
                            Reset to Default
                          </button>
                        )}
                        <span className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-medium">
                          {isCustomPassword ? "Custom" : "Default"}
                        </span>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10 transition-colors font-mono"
                        placeholder="Client password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Default: Name (no spaces) + last 5 digits of phone number. Admin can edit this manually.
                    </p>
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="opted" className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Services
                    </label>
                    <Select
                      id="opted"
                      options={serviceOptions}
                      isMulti
                      onChange={handleSelectChange}
                      styles={selectStyles}
                      className="text-sm"
                      placeholder="Select services"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-6 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-6 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Create Client
                </button>
              </div>
            </form>
          </div>
        </div>
      </MainSection>
    </div>
  );
}

export default AddClient;
